import { useBuildWiseModules } from '@buildwise/libs/helpers';
import {
  ArticleCreationMethods,
  NotifiedUsersModal,
  ParseFileActions,
  WriteWithAIActionButtons,
  WriteWithAIFormValues,
  WriteWithAIOnSelectOptions,
} from '@buildwise/libs/posts';
import WeWillWriteActions from '@buildwise/libs/posts/components/WeWillWrite/WeWillWriteActions';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import { UserType } from 'hooks/useUserData';
import { Dispatch, SetStateAction, useCallback, useMemo, useState, ChangeEvent } from 'react';
import { FormProvider, useFormContext, UseFormReturn, useWatch } from 'react-hook-form';
import GroupLineIcon from 'remixicon-react/GroupLineIcon';

type UsersSearchResponseType = {
  totalElements?: number;
  totalNumber?: number;
  totalPages?: number;
  userResponse: UserType[];
};

type PostFormDialogActionsProps = {
  article: ArticleType | undefined;
  articleCreationMethod: ArticleCreationMethods;
  articleType: ArticleSourceType;
  canPublishPost: boolean;
  createPlaylist: boolean;
  forward: boolean;
  isCreateDraft: boolean;
  isEditing: boolean;
  isOnePostSelected: boolean;
  isPublishingFileAsOnePost: boolean;
  isSavingDraft: boolean;
  loadingFiles: boolean;
  response: any;
  startAnotherPost: boolean;
  writeWithAIFormState: UseFormReturn<WriteWithAIFormValues>;
  downloadedFiles: AttachmentType[];
  onForward: () => void;
  onGenerate: () => void;
  onParseFile: (options: { isOnePostSelected: boolean }) => Promise<void>;
  onSaveDraft: () => void;
  onSelect: (options: WriteWithAIOnSelectOptions) => void;
  onSubmit: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setPublishingFileAsOnePost: Dispatch<SetStateAction<boolean>>;
  setResponse: any;
  setStartAnotherPost: Dispatch<React.SetStateAction<boolean>>;
};

function PostFormDialogActions({
  article,
  articleCreationMethod,
  articleType,
  canPublishPost,
  createPlaylist,
  forward,
  isCreateDraft,
  isSavingDraft,
  isEditing,
  isOnePostSelected,
  isPublishingFileAsOnePost,
  loadingFiles,
  response,
  writeWithAIFormState,
  downloadedFiles,
  onForward,
  onGenerate,
  onParseFile,
  onSaveDraft,
  onSelect,
  onSubmit,
  setOpen,
  setPublishingFileAsOnePost,
  setResponse,
}: PostFormDialogActionsProps) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<PostFormData>();
  const projectSpecific = useWatch({
    control,
    name: 'projectSpecific',
    defaultValue: true,
  });
  const isWritingWithAI = useWatch({ control, name: 'isWritingWithAI', defaultValue: false });

  // NOTE : It is used to show the notified users for Questions
  // const departments: string[] = useWatch({ control, name: 'targetGroups', defaultValue: [] });
  // const regions: string[] = useWatch({ control, name: 'region', defaultValue: [] });
  // const [openNotifiedUsers, setOpenNotifiedUsers] = useState(false);
  // const { data: peopleData } = useSearchItemQuery<UsersSearchResponseType>(
  //   {
  //     searchType: 'users',
  //     operator: 'or',
  //     filterBy: {
  //       filters: [
  //         {
  //           filterType: 'name',
  //           filterValue: 'ALL',
  //         },
  //       ],
  //     },
  //   },
  //   articleType === ArticleSourceType.QUESTION,
  // );
  // const users = useMemo(() => {
  //   if (!(departments?.length || regions?.length)) return [];

  //   const filterByCriteria = (_user: UserType) => {
  //     const matchesDepartments = departments.some((value) => _user?.department?.includes(value));
  //     const matchesRegions = regions.some((value) => _user?.region?.includes(value));

  //     return (
  //       ((departments.length && matchesDepartments) || (regions.length && matchesRegions)) &&
  //       (!departments.length || !regions.length || (matchesDepartments && matchesRegions))
  //     );
  //   };

  //   return (peopleData?.userResponse ?? []).filter(filterByCriteria);
  // }, [departments, regions, peopleData?.userResponse]);

  const { singularCollectionsModuleName } = useBuildWiseModules();

  const handleCheckboxChange = useCallback((_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setValue('projectSpecific', !checked, { shouldDirty: true, shouldValidate: true });
  }, []);

  return (
    <>
      {forward ? (
        <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%">
          <Button disabled={isSubmitting || loadingFiles} onClick={onForward} variant="contained">
            {isSubmitting ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : (
              'Forward'
            )}
          </Button>
        </Box>
      ) : (
        <>
          {(articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY ||
            articleType === ArticleSourceType.LESSON ||
            (articleCreationMethod !== ArticleCreationMethods.PARSE_FILE && isWritingWithAI) ||
            isPublishingFileAsOnePost) && (
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" ml={-1}>
                {isEditing && article?.isDraft && (
                  <Button disabled={isSubmitting || loadingFiles} onClick={onSaveDraft}>
                    {isSubmitting && isSavingDraft ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : (
                      'Update Draft'
                    )}
                  </Button>
                )}
                {!isEditing && !isCreateDraft && (
                  <Button disabled={isSubmitting || loadingFiles} onClick={onSaveDraft}>
                    {isSubmitting && isSavingDraft ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : (
                      'Save as Draft'
                    )}
                  </Button>
                )}
                {articleType === ArticleSourceType.LESSON && (
                  <FormControlLabel
                    control={<Checkbox onChange={handleCheckboxChange} disabled={isSubmitting} />}
                    label="Show on Feed"
                    checked={!projectSpecific}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                {isCreateDraft ? (
                  <Button
                    disabled={isSubmitting || loadingFiles}
                    onClick={onSaveDraft}
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : (
                      'Save Draft'
                    )}
                  </Button>
                ) : !isCreateDraft && isEditing ? (
                  (!article?.isDraft || canPublishPost) && (
                    <>
                      <Button
                        disabled={isSubmitting || loadingFiles}
                        onClick={onSubmit}
                        variant="contained"
                      >
                        {isSubmitting && !isSavingDraft ? (
                          <CircularProgress
                            sx={(theme) => ({ color: theme.palette.custom.black })}
                            size={20}
                          />
                        ) : article?.isDraft ? (
                          articleType === ArticleSourceType.QUESTION ||
                          articleType === ArticleSourceType.SUGGESTION ||
                          articleType === ArticleSourceType.LESSON ? (
                            'Submit'
                          ) : article.originIndicator === 'PLAYLIST' ? (
                            `Create ${singularCollectionsModuleName}`
                          ) : (
                            'Post'
                          )
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    {/* {!createPlaylist &&
                      articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY &&
                      articleType !== ArticleSourceType.QUESTION &&
                      articleType !== ArticleSourceType.SUGGESTION && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mr: 2,
                          }}
                        >
                          <Checkbox
                            onChange={(event) => {
                              setStartAnotherPost(event.target.checked);
                            }}
                            checked={startAnotherPost}
                          />
                          <Typography>Start another post</Typography>
                        </Box>
                      )} */}
                    {/* {articleType === ArticleSourceType.QUESTION && (
                      <>
                        <Tooltip
                          placement="top"
                          title={
                            <Typography fontSize={14}>
                              {users.length} members will be notified about this question
                            </Typography>
                          }
                        >
                          <Box sx={{ display: 'flex', mt: 0.5, cursor: 'default' }}>
                            <GroupLineIcon size={18} />
                            <Typography
                              sx={(theme) => ({
                                ml: 0.5,
                                color: theme.palette.custom.mistBlue,
                                fontSize: 14,
                              })}
                            >
                              {users.length}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </>
                    )} */}
                    {canPublishPost && (
                      <>
                        <Button
                          disabled={isSubmitting || loadingFiles}
                          onClick={onSubmit}
                          variant="contained"
                        >
                          {isSubmitting && !isSavingDraft ? (
                            <CircularProgress
                              sx={(theme) => ({ color: theme.palette.custom.black })}
                              size={20}
                            />
                          ) : createPlaylist ? (
                            `Create ${singularCollectionsModuleName}`
                          ) : articleType === ArticleSourceType.QUESTION ||
                            articleType === ArticleSourceType.SUGGESTION ||
                            articleType === ArticleSourceType.LESSON ? (
                            'Submit'
                          ) : (
                            'Post'
                          )}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}
          {articleCreationMethod === ArticleCreationMethods.WRITE_WITH_AI && !isWritingWithAI && (
            <FormProvider {...writeWithAIFormState}>
              <WriteWithAIActionButtons onGenerate={onGenerate} onSelect={onSelect} />
            </FormProvider>
          )}
          {articleCreationMethod === ArticleCreationMethods.PARSE_FILE &&
            !isPublishingFileAsOnePost && (
              <ParseFileActions
                downloadedFiles={downloadedFiles}
                setResponse={setResponse}
                response={response}
                onParseFile={onParseFile}
                setOpen={setOpen}
                isOnePostSelected={isOnePostSelected}
                setPublishingFileAsOnePost={setPublishingFileAsOnePost}
              />
            )}
          {articleCreationMethod === ArticleCreationMethods.WE_WILL_WRITE && (
            <WeWillWriteActions setOpen={setOpen} />
          )}
        </>
      )}

      {/* {openNotifiedUsers && (
        <NotifiedUsersModal open={openNotifiedUsers} users={users} setOpen={setOpenNotifiedUsers} />
      )} */}
    </>
  );
}

export default PostFormDialogActions;
